import * as L from 'leaflet';
import 'leaflet-draw';

import {
  closeRing,
  DrawnShape,
  isShapeInside,
  LngLat,
  pointInRing,
  POINT_TOLERANCE_M,
} from './map-draw.model';

/** The three shapes a boundary-constrained pick can produce. */
export type MapDrawTool = 'point' | 'rectangle' | 'polygon';

export interface MapDrawOptions {
  /** Which tools the toolbar offers. Defaults to all three. */
  tools?: MapDrawTool[];
  /**
   * The area a shape must stay inside. Everything outside it is dimmed, and a
   * shape that leaves it is refused rather than accepted and complained about
   * later. Null lifts the constraint entirely.
   */
  boundary?: DrawnShape | null;
  /** Accent for the drawn shape. Defaults to the app's blue. */
  color?: string;
  /** Draw a disc showing how far a marked place actually reaches. */
  showPointTolerance?: boolean;
  /** The shape changed — drawn, dragged, cleared. */
  onChange?: (shape: DrawnShape | null) => void;
  /** A shape was refused, with the reason to show the user. */
  onReject?: (reason: string) => void;
  /** The armed tool changed, including to null when a draw finishes or cancels. */
  onToolChange?: (tool: MapDrawTool | null) => void;
}

/** Leaflet wants `[lat, lng]`; everything else in this app is `[lng, lat]`. */
const toLatLng = ([lng, lat]: LngLat): L.LatLngTuple => [lat, lng];
const fromLatLng = (ll: L.LatLng): LngLat => [ll.lng, ll.lat];

/** A ring big enough to be "everywhere", for the mask's outer boundary. */
const WORLD: L.LatLngTuple[] = [
  [-89.9, -180],
  [-89.9, 180],
  [89.9, 180],
  [89.9, -180],
];

/**
 * DRAW ONE SHAPE INSIDE A BOUNDARY — the Leaflet half of a "mark the place"
 * control.
 *
 * THE TOOLS ARE ON THE MAP, AND THEY ARE LEAFLET-DRAW'S OWN.
 * `L.Control.Draw` sits in the map's top-left corner and its buttons carry the
 * app's `custom-button-leaflet` class, which is exactly what the tasking map
 * does to its own draw toolbar — same stack, same dark chrome, same artwork —
 * with the reset button at the foot of it. Two earlier versions of this file
 * got that wrong in opposite directions: one built `new L.Draw.Rectangle(map,…)`
 * by hand, which skips the option merge, the `drawLocal` lookups and the
 * touch-icon swap that `L.DrawToolbar` does for you and throws before the tool
 * is ever armed; the next kept the control but hid it behind a tool row above
 * the map, which worked and still looked like nothing else in the app.
 *
 * A TOOL LASTS ONE SHAPE. Arming is a click on the toolbar, and leaflet-draw
 * disarms itself once the shape is finished — which is what hands the map back
 * for panning, and is why nothing is armed when the map opens either. An
 * earlier version kept the tool armed across draws; it made a redraw one click
 * cheaper and made panning impossible until you noticed the lit button.
 *
 * WHICH TOOL IS ARMED is read from leaflet-draw's own DRAWSTART and DRAWSTOP
 * events rather than tracked at the call that armed it. Both fire from inside
 * the handler's `enable()`/`disable()`, so every route into a tool — a toolbar
 * click, a tool finishing, Escape — reports the same way, and the caller has
 * one place to believe. Tracking it at the call site instead is what made the
 * buttons look dead in an earlier version of this.
 *
 * WHY A CONTROLLER AND NOT MORE INPUTS ON THE WRAPPER.
 * `LeafletMapWrapperComponent` renders geometry and does it well — normalising
 * WKT, fitting bounds, surviving a dialog that sizes late. Drawing is a second
 * job with its own state, and bolting it on would put two unrelated lifecycles
 * in one component that already has a live consumer. This takes the `L.Map` the
 * wrapper emits from `(mapReady)` and adds its own layers beside the wrapper's,
 * which the wrapper never touches — it only ever removes the one layer it
 * rendered itself.
 *
 * THE BOUNDARY IS THE POINT. Everything outside it is dimmed by a masked
 * polygon, and any shape that leaves it is refused at the moment it is drawn.
 * That is the whole difference from the flow this replaces, where a coordinate
 * outside the AOI was accepted, carried through a dialog close, and answered
 * with a modal that then discarded the user's entire selection.
 */
export class MapDrawController {
  private readonly opts: MapDrawOptions;
  private readonly group = L.featureGroup();
  private mask: L.Polygon | null = null;
  private toolbar: any = null;
  private toolbarContainer: HTMLElement | null = null;
  private resetControl: any = null;
  private activeTool: MapDrawTool | null = null;
  private lockedTool: MapDrawTool | null = null;

  private layer: L.Layer | null = null;
  private toleranceRing: L.Circle | null = null;
  private current: DrawnShape | null = null;

  constructor(private readonly map: L.Map, options: MapDrawOptions) {
    // Area tools lead, because an area is what a boundary-constrained pick
    // usually is — and this order is the toolbar's order, see `addToolbar`.
    this.opts = { tools: ['rectangle', 'polygon', 'point'], showPointTolerance: true, ...options };
    this.group.addTo(this.map);
    this.drawMask();
    this.addToolbar();
    this.addResetControl();
    this.map.on((L as any).Draw.Event.CREATED, this.onCreated);
    // Arming is whatever leaflet-draw says it is: both events fire from the
    // handler's own `enable()`/`disable()`, so nothing else has to be told
    // which tool is live — and nothing else can disagree about it.
    this.map.on((L as any).Draw.Event.DRAWSTART, this.onDrawStart);
    this.map.on((L as any).Draw.Event.DRAWSTOP, this.onDrawStop);
  }

  /**
   * Disarm whatever the toolbar has armed, without touching what is drawn.
   * DRAWSTOP records the result, so the caller hears about it the same way it
   * hears about a tool going out on its own.
   */
  cancelTool(): void {
    try {
      this.activeMode()?.handler?.disable();
    } catch {
      // A handler mid-teardown is not worth a thrown error.
    }
  }

  /**
   * Put a shape on the map without drawing it — the typed field's way in, so a
   * pasted coordinate and a drawn one end up as the same thing.
   *
   * Silent by design: it does NOT call `onChange`, because the caller already
   * knows (it supplied the shape), and echoing back into a two-way binding is
   * how these loops start.
   */
  setShape(shape: DrawnShape | null, opts: { fit?: boolean } = {}): void {
    this.clearShape();
    this.current = shape;
    if (!shape) {
      this.setLockedTool(null);
      return;
    }
    this.layer = this.buildLayer(shape);
    if (!this.layer) {
      this.current = null;
      return;
    }
    this.group.addLayer(this.layer);
    this.addTolerance(shape);
    if (opts.fit) this.fitTo(shape);
  }

  /** Lock selection tools once a valid value has been accepted from either input source. */
  lockTools(shape: DrawnShape | null = this.current): void {
    const fallback = shape?.kind === 'point'
      ? 'point'
      : this.opts.tools?.includes('polygon')
        ? 'polygon'
        : this.opts.tools?.includes('rectangle')
          ? 'rectangle'
          : 'point';
    this.setLockedTool(this.lockedTool ?? fallback);
  }

  /** Take the shape off the map and report the emptiness. */
  clear(): void {
    this.clearShape();
    this.current = null;
    this.setLockedTool(null);
    this.opts.onChange?.(null);
  }

  /** Frame the boundary — "fit to area". Falls back to the shape when unbounded. */
  fitToBoundary(): void {
    const target = this.opts.boundary ?? this.current;
    if (target) this.fitTo(target);
  }

  /** Every layer, control and listener this added, gone. The map is the caller's. */
  destroy(): void {
    this.cancelTool();
    this.map.off((L as any).Draw.Event.CREATED, this.onCreated);
    this.map.off((L as any).Draw.Event.DRAWSTART, this.onDrawStart);
    this.map.off((L as any).Draw.Event.DRAWSTOP, this.onDrawStop);
    this.clearShape();
    try {
      if (this.resetControl) this.map.removeControl(this.resetControl);
      if (this.toolbar) this.map.removeControl(this.toolbar);
      if (this.mask) this.map.removeLayer(this.mask);
      this.map.removeLayer(this.group);
    } catch {
      // The map may already be torn down by its own component.
    }
    this.toolbar = null;
    this.resetControl = null;
    this.mask = null;
  }

  // ── internals ─────────────────────────────────────────────────────────────

  /**
   * The draw control, carrying only the tools the caller offers, in the order
   * the caller lists them. Its icons are the plugin's sprite; every screen in
   * this app replaces those in component-scoped CSS, and the caller does the
   * same here with the app's own `assets/map-icons` artwork — which is why the
   * buttons themselves stay leaflet-draw's rather than being rebuilt.
   *
   * `false` rather than omission for the rest: leaflet-draw reads each key and
   * shows a button unless it is explicitly false. No `edit` section — the
   * tasking map does not have one either, and a shape is redrawn rather than
   * re-cornered. A marked POINT stays draggable, which covers the nudge that
   * edit mode would otherwise be needed for.
   */
  private addToolbar(): void {
    const offered = this.opts.tools ?? [];
    const shapeOptions: L.PathOptions = {
      color: this.accent(),
      weight: 2,
      fillColor: this.accent(),
      fillOpacity: 0.16,
    };

    this.toolbar = new (L as any).Control.Draw({
      position: 'topleft',
      draw: {
        marker: offered.includes('point') ? {} : false,
        rectangle: offered.includes('rectangle') ? { shapeOptions, showArea: false } : false,
        polygon: offered.includes('polygon')
          ? {
              shapeOptions,
              showArea: false,
              // A self-crossing ring has no honest inside, and the containment
              // test below would answer differently depending on which lobe was
              // tested.
              allowIntersection: false,
            }
          : false,
        circle: false,
        circlemarker: false,
        polyline: false,
      },
    });
    this.map.addControl(this.toolbar);

    // What makes it look like the rest of the app: `leaflet-task` adds this same
    // class to every link in its own draw toolbar, and the dark translucent
    // square with the #444 border comes from it. The icons are swapped for the
    // app's `assets/map-icons` artwork in the caller's stylesheet, the way that
    // screen does it too — leaflet-draw's own sprite is never used here.
    const container: HTMLElement | undefined = this.toolbar?.getContainer?.();
    this.toolbarContainer = container ?? null;
    const links = container ? Array.from(container.querySelectorAll('a')) : [];
    links.forEach((link) => link.classList.add('custom-button-leaflet'));

    // leaflet-draw renders its buttons in ITS order (polyline, polygon,
    // rectangle, circle, marker, circlemarker), which is not the order a caller
    // lists its tools in — and the caller's order is the meaningful one: it
    // leads with the shape this map is usually asked for. Re-appending the
    // links in that order is enough; a parent re-appending a child it already
    // has just moves it.
    const toolTitles: Record<MapDrawTool, string> = {
      point: 'Place point of interest (POI) as point',
      rectangle: 'Draw area of interest (AOI) as rectangle',
      polygon: 'Draw area of interest (AOI) as polygon',
    };

    offered.forEach((tool) => {
      const link = container?.querySelector(
        `a.leaflet-draw-draw-${this.drawClassFor(tool)}`
      ) as HTMLAnchorElement | null;
      if (link) {
        const title = toolTitles[tool];
        link.title = title;
        link.setAttribute('aria-label', title);
      }
      link?.parentElement?.appendChild(link);
    });
    this.updateToolbarLockState();
  }

  /** The suffix leaflet-draw puts on a tool's button class. */
  private drawClassFor(tool: MapDrawTool): string {
    return tool === 'point' ? 'marker' : tool;
  }

  private activeMode(): any {
    return this.toolbar?._toolbars?.draw?._activeMode ?? null;
  }

  /**
   * RESET — at the foot of the tool stack, where the tasking map keeps its own,
   * and doing what that one does: wipe what is drawn AND frame the target
   * again. One button, because "clear it" and "I have lost the target" are the
   * same moment in practice, and a fourth button in a dialog-sized map that
   * only did half of it would be a button too many.
   */
  private addResetControl(): void {
    const Reset = (L as any).Control.extend({
      options: { position: 'topleft' },
      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control map-draw-reset');
        const link = L.DomUtil.create(
          'a',
          'custom-button-leaflet',
          container
        ) as HTMLAnchorElement;
        link.href = '#';
        link.title = 'Clear';
        link.setAttribute('role', 'button');
        link.innerHTML = '<img src="assets/map-icons/reset.svg" alt="" />';
        L.DomEvent.on(link, 'click', (event: any) => {
          L.DomEvent.stop(event);
          this.clear();
          this.fitToBoundary();
        });
        // Without this a drag started on the button pans the map underneath it.
        L.DomEvent.disableClickPropagation(container);
        return container;
      },
    });
    this.resetControl = new Reset();
    this.map.addControl(this.resetControl);
  }

  /**
   * A world-sized polygon with the boundary punched out of it, so the only lit
   * part of the map is the part a shape may occupy.
   *
   * `interactive: false` deliberately: the mask must not swallow clicks, or the
   * draw handlers never see them. A click outside is REFUSED with a reason
   * rather than ignored — silence would read as a broken map.
   */
  private drawMask(): void {
    const boundary = this.opts.boundary;
    if (!boundary || boundary.kind !== 'area' || !boundary.ring) return;
    this.mask = L.polygon([WORLD, closeRing(boundary.ring).map(toLatLng)], {
      color: 'transparent',
      weight: 0,
      fillColor: '#0b1220',
      fillOpacity: 0.42,
      interactive: false,
      className: 'map-draw-mask',
    }).addTo(this.map);
  }

  private accent(): string {
    return this.opts.color ?? '#1059a9';
  }

  /**
   * A shape came off the toolbar. Refused outright when it leaves the boundary
   * — the drawn layer is never added, so there is nothing on the map that the
   * value does not describe.
   */
  /** A tool was armed from the toolbar. */
  private readonly onDrawStart = (event: any): void => {
    const tool = this.toolFromType(event?.layerType);
    if (tool && this.lockedTool) {
      this.cancelTool();
      return;
    }
    if (!tool || this.activeTool === tool) return;
    this.activeTool = tool;
    this.opts.onToolChange?.(tool);
  };

  /**
   * The handler went away — a shape was finished, Escape was pressed, or the
   * lit button was clicked again. All three mean the same thing to the caller:
   * no tool, and the map is back to panning.
   */
  private readonly onDrawStop = (): void => {
    if (!this.activeTool) return;
    this.activeTool = null;
    this.opts.onToolChange?.(null);
  };

  /** leaflet-draw's `layerType` in this class's vocabulary. */
  private toolFromType(type: string | undefined): MapDrawTool | null {
    if (type === 'marker') return 'point';
    if (type === 'rectangle' || type === 'polygon') return type;
    return null;
  }

  private readonly onCreated = (event: any): void => {
    const shape = this.shapeFromDrawEvent(event);
    if (!shape) return;

    const usedTool = this.toolFromType(event?.layerType);
    if (usedTool && this.lockedTool && usedTool !== this.lockedTool) {
      this.opts.onReject?.('Reset the current selection before using another drawing tool.');
      return;
    }

    if (!isShapeInside(shape, this.opts.boundary ?? null)) {
      this.opts.onReject?.(this.rejectionFor(shape, this.opts.boundary ?? null));
      return;
    }

    this.setShape(shape);
    if (usedTool) this.setLockedTool(usedTool);
    this.opts.onChange?.(shape);
  };

  private setLockedTool(tool: MapDrawTool | null): void {
    if (this.lockedTool === tool) return;
    this.lockedTool = tool;
    this.updateToolbarLockState();
  }

  private updateToolbarLockState(): void {
    const container = this.toolbarContainer;
    if (!container) return;

    const offered = this.opts.tools ?? [];
    offered.forEach((tool) => {
      const link = container.querySelector(`a.leaflet-draw-draw-${this.drawClassFor(tool)}`) as HTMLAnchorElement | null;
      if (!link) return;

      const disable = !!this.lockedTool;
      link.style.pointerEvents = disable ? 'none' : '';
      link.style.opacity = disable ? '0.45' : '';
      if (disable) {
        link.setAttribute('aria-disabled', 'true');
      } else {
        link.removeAttribute('aria-disabled');
      }
    });
  }

  private rejectionFor(shape: DrawnShape, boundary: DrawnShape | null): string {
    const insideMsg = 'Select your observation inside the boundary.';
    const partialAreaMsg = 'Part of the observation area is outside the boundary. Draw the area inside.';

    if (shape.kind === 'point') return insideMsg;
    if (shape.kind !== 'area' || !shape.ring) return partialAreaMsg;
    if (!boundary || boundary.kind !== 'area' || !boundary.ring || boundary.ring.length < 4) {
      return partialAreaMsg;
    }

    const ring = closeRing(shape.ring);
    const corners = ring.slice(0, -1);
    if (!corners.length) return partialAreaMsg;

    const insideCount = corners.filter(([lng, lat]) => pointInRing(lng, lat, boundary.ring!)).length;

    if (insideCount === 0) return insideMsg;
    if (insideCount < corners.length) return partialAreaMsg;
    return partialAreaMsg;
  }

  private shapeFromDrawEvent(event: any): DrawnShape | null {
    const layer = event?.layer;
    if (!layer) return null;

    if (typeof layer.getLatLng === 'function') {
      return { kind: 'point', point: fromLatLng(layer.getLatLng()) };
    }
    if (typeof layer.getLatLngs === 'function') {
      const ring = this.ringFromLayer(layer);
      return ring.length >= 3 ? { kind: 'area', ring: closeRing(ring) } : null;
    }
    return null;
  }

  private ringFromLayer(layer: any): LngLat[] {
    const latlngs = layer.getLatLngs?.() ?? [];
    // Leaflet nests a polygon's rings one level deep; a rectangle is the same
    // shape. Only the outer ring is ever used.
    const outer = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
    return (outer as L.LatLng[]).map(fromLatLng);
  }

  private buildLayer(shape: DrawnShape): L.Layer | null {
    if (shape.kind === 'point' && shape.point) {
      const marker = L.marker(toLatLng(shape.point), { draggable: true, autoPan: true });
      marker.on('dragend', () => this.onMarkerDragged(marker));
      return marker;
    }
    if (shape.kind === 'area' && shape.ring) {
      return L.polygon(closeRing(shape.ring).map(toLatLng), {
        color: this.accent(),
        weight: 2,
        fillColor: this.accent(),
        fillOpacity: 0.16,
      });
    }
    return null;
  }

  /**
   * The disc a marked place actually covers. Drawn from the same constant the
   * matcher uses, so what is shown is the rule rather than an illustration of
   * it — a bare pin says "here" where the rule says "within 500 m of here".
   */
  private addTolerance(shape: DrawnShape): void {
    if (!this.opts.showPointTolerance || shape.kind !== 'point' || !shape.point) return;
    this.toleranceRing = L.circle(toLatLng(shape.point), {
      radius: POINT_TOLERANCE_M,
      color: this.accent(),
      weight: 1,
      dashArray: '4 3',
      fillColor: this.accent(),
      fillOpacity: 0.08,
      interactive: false,
    });
    this.group.addLayer(this.toleranceRing);
  }

  /** Nudged by hand. Snapped back if the nudge left the boundary. */
  private onMarkerDragged(marker: L.Marker): void {
    const next: DrawnShape = { kind: 'point', point: fromLatLng(marker.getLatLng()) };
    if (!isShapeInside(next, this.opts.boundary ?? null)) {
      this.opts.onReject?.(this.rejectionFor(next, this.opts.boundary ?? null));
      if (this.current) this.setShape(this.current);
      return;
    }
    this.current = next;
    if (next.point && this.toleranceRing) {
      this.toleranceRing.setLatLng(toLatLng(next.point));
    }
    this.opts.onChange?.(next);
  }

  private clearShape(): void {
    if (this.layer) this.group.removeLayer(this.layer);
    if (this.toleranceRing) this.group.removeLayer(this.toleranceRing);
    this.layer = null;
    this.toleranceRing = null;
  }

  private fitTo(shape: DrawnShape): void {
    if (shape.kind === 'point' && shape.point) {
      this.map.setView(toLatLng(shape.point), Math.max(this.map.getZoom() ?? 0, 11));
      return;
    }
    if (shape.kind === 'area' && shape.ring) {
      const bounds = L.latLngBounds(closeRing(shape.ring).map(toLatLng));
      if (bounds.isValid()) this.map.fitBounds(bounds, { padding: [24, 24] });
    }
  }
}
